import { useState, useContext, useEffect } from "react"
import { gql, useQuery } from "@apollo/client"
import { classNames } from "primereact/utils"
import { ProgressSpinner } from "primereact/progressspinner"
import _ from "lodash"
import defaultLogo from "../../assets/images/default-logo2.jpg"

// Context
import { UserContext } from "../../context/userContext"
import { FirebaseContext } from "../../firebase"

import styles from "../../styles/OrganizationLogo.module.css"

const GET_ORGANIZATION = gql`
  query getOrganization($staff_id: ID!, $organization_id: ID!) {
    organization(staff_id: $staff_id, organization_id: $organization_id) {
      organization_id
      organization_name
      organization_logo
    }
  }
`

export default function OrganizationLogo({
  className = "",
  imgStyle,
  alt = "",
  renderWhenNoLogo = true,
  noLogoVariant = "default",
  onHasLogoChange,
}) {
  const userCtx = useContext(UserContext)
  const firebase = useContext(FirebaseContext)
  const [loading, setLoading] = useState(true)
  const [logoUrl, setLogoUrl] = useState(defaultLogo)
  const [hasLogo, setHasLogo] = useState(null)
  const [organizationName, setOrganizationName] = useState("")

  const { getStorage, ref, getDownloadURL } = firebase.storage
  const storage = getStorage()

  useEffect(() => {
    if (typeof onHasLogoChange === "function" && hasLogo !== null) {
      onHasLogoChange(hasLogo)
    }
  }, [hasLogo, onHasLogoChange])

  useQuery(GET_ORGANIZATION, {
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
    },
    fetchPolicy: "cache-first",
    onCompleted: async ({ organization }) => {
      setLoading(true)
      setOrganizationName(_.get(organization, "organization_name", ""))

      const getLogoUrl = async () => {
        const organizationLogo = organization?.organization_logo

        if (!_.isEmpty(organizationLogo)) {
          const logoRef = ref(storage, organizationLogo)
          return await getDownloadURL(logoRef)
        }
        return null
      }

      const fetchLogo = async () => {
        try {
          const logo = await getLogoUrl()
          const hasOrganizationLogo = !_.isEmpty(logo)

          setHasLogo(hasOrganizationLogo)
          setLogoUrl(hasOrganizationLogo ? logo : defaultLogo)
        } catch (error) {
          setHasLogo(false)
          setLogoUrl(defaultLogo)
        } finally {
          setLoading(false)
        }
      }

      fetchLogo()
    },
    onError: () => {
      setHasLogo(false)
      setLogoUrl(defaultLogo)
      setLoading(false)
    },
  })

  if (loading) {
    return (
      <div
        className={classNames(className, styles.defaultFrame, styles.loadingContainer)}
        aria-label={alt || "Loading organization logo"}
        style={imgStyle}
      >
        <ProgressSpinner
          className={styles.loadingSpinner}
          strokeWidth="4"
          animationDuration=".4s"
        />
      </div>
    )
  }

  if (hasLogo === false && !renderWhenNoLogo) {
    return null
  }

  if (hasLogo === false && renderWhenNoLogo && noLogoVariant === "initials") {
    const words = (organizationName || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)

    let initials = "ORG"

    if (words.length === 1) {
      initials = words[0].slice(0, 2).toUpperCase()
    } else if (words.length > 1) {
      initials = `${words[0][0] || ""}${words[1][0] || ""}`.toUpperCase()
    }

    return (
      <div
        className={classNames(className, styles.defaultFrame, styles.initialsContainer)}
        aria-label={alt || organizationName || "Organization"}
        style={imgStyle}
      >
        {initials}
      </div>
    )
  }

  return (
    <img
      className={classNames(className, styles.defaultFrame)}
      src={logoUrl}
      alt={alt}
      style={imgStyle}
    />
  )
}
